import type { OmMenuItem } from '@owma/kit'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TodoItem } from '../../../modules/TodoItem'
import { TodoSection } from '../../../modules/TodoSection'
import { updateTodoDueDate, updateTodoRecurrence } from '../../../modules/TodoUpdater'
import { DatePickerCallbacks, FolderDisplayData, TodoRenderDataItem } from '../types'

type ContextMenuItems = OmMenuItem[]

interface UseTodoRenderDataReturn {
  todoRenderDataList: TodoRenderDataItem[]
  folderDataMap: Map<string, FolderDisplayData>
  contextMenuItemsMap: Map<string, ContextMenuItems>
  handleDueDateChange: (todo: TodoItem, dueDateKey: string) => void
}

/**
 * TodoSectionListView의 rendering data를 준비하는 hook
 */
export const useTodoRenderData = (
  sections: TodoSection[],
  collapsedKeys: string[],
  getFolderName: (folderUrl: string) => string,
  getFolderColor: (folderUrl: string) => string,
  getFolderIsReadOnly: (folderUrl: string) => boolean,
  onTodoUpdate?: () => void,
  onTodoDelete?: (todo: TodoItem) => void,
  datePickerCallbacks: DatePickerCallbacks = {},
): UseTodoRenderDataReturn => {
  const { t } = useTranslation()

  // 삭제 핸들러 - confirm dialog는 상위에서 처리
  const handleDelete = useCallback(
    (todo: TodoItem) => {
      if (onTodoDelete) {
        onTodoDelete(todo)
      }
    },
    [onTodoDelete],
  )

  // Due date 변경 핸들러
  const handleDueDateChange = useCallback(
    (todo: TodoItem, dueDateKey: string) => {
      if (!todo) return

      let selectedDate: Date | null = null
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)

      today.setHours(0, 0, 0, 0)
      tomorrow.setHours(0, 0, 0, 0)

      switch (dueDateKey) {
        case 'dueToday':
          selectedDate = new Date(today)
          break
        case 'dueTomorrow':
          selectedDate = new Date(tomorrow)
          break
        case 'noDate':
          selectedDate = null
          break
        default:
          console.warn(`Unknown due date key: ${dueDateKey}`)
          return
      }

      if (todo.recurrenceRule) {
        updateTodoRecurrence(todo, null, selectedDate, (success: boolean) => {
          if (success && onTodoUpdate) {
            onTodoUpdate()
          }
        })
      } else {
        updateTodoDueDate(todo, selectedDate, (success: boolean) => {
          if (success && onTodoUpdate) {
            onTodoUpdate()
          }
        })
      }
    },
    [onTodoUpdate],
  )

  // Context menu items 생성
  const getContextMenuItems = useCallback(
    (todo: TodoItem): ContextMenuItems => [
      {
        key: 'dueToday',
        label: t('todo.todolist.dueToday'),
        onClick: (e) => {
          e.domEvent.stopPropagation()
          handleDueDateChange(todo, 'dueToday')
        },
      },
      {
        key: 'dueTomorrow',
        label: t('todo.todolist.dueTomorrow'),
        onClick: (e) => {
          e.domEvent.stopPropagation()
          handleDueDateChange(todo, 'dueTomorrow')
        },
      },
      {
        key: 'noDate',
        label: t('todo.todolist.noDate'),
        onClick: (e) => {
          e.domEvent.stopPropagation()
          handleDueDateChange(todo, 'noDate')
        },
      },
      {
        key: 'deleteDivider',
        type: 'divider',
      },
      {
        key: 'delete',
        label: t('common.button.delete'),
        onClick: (e) => {
          e.domEvent.stopPropagation()
          handleDelete(todo)
        },
        style: { color: 'red' },
      },
    ],
    [t, handleDueDateChange, handleDelete],
  )

  // 폴더 데이터 맵 (성능 최적화를 위해 미리 계산)
  const folderDataMap = useMemo(() => {
    const map = new Map<string, FolderDisplayData>()
    const folderUrls = new Set<string>()

    sections.forEach((section) => {
      section.todos?.forEach((todo) => {
        if (todo.folderUrl) {
          folderUrls.add(todo.folderUrl)
        }
      })
    })

    folderUrls.forEach((folderUrl) => {
      map.set(folderUrl, {
        displayName: getFolderName(folderUrl),
        color: getFolderColor(folderUrl),
        isReadOnly: getFolderIsReadOnly(folderUrl),
      })
    })

    return map
  }, [sections, getFolderName, getFolderColor, getFolderIsReadOnly])

  // Context menu items 맵 (todo별 메모이제이션)
  const contextMenuItemsMap = useMemo(() => {
    const map = new Map<string, ContextMenuItems>()

    sections.forEach((section) => {
      section.todos?.forEach((todo) => {
        if (todo.url || todo.uid) {
          map.set(todo.url || todo.uid, getContextMenuItems(todo))
        }
      })
    })

    return map
  }, [sections, getContextMenuItems])

  // 최종 렌더링용 리스트 데이터 생성
  const todoRenderDataList = useMemo(() => {
    const data: TodoRenderDataItem[] = []
    sections.forEach((section) => {
      if (section.title) {
        data.push({
          type: 'section',
          key: `section-${section.key}`,
          section,
        })
      }

      if (!collapsedKeys.includes(section.key) || !section.title) {
        section.todos.forEach((todo) => {
          const todoKey = todo.url || todo.uid
          data.push({
            type: 'todo',
            key: todoKey,
            todo,
            sectionKey: section.key,
            folderData: folderDataMap.get(todo.folderUrl),
            contextMenuItems: contextMenuItemsMap.get(todoKey),
            datePickerCallbacks,
          })
        })
      }
    })
    return data
  }, [sections, collapsedKeys, folderDataMap, contextMenuItemsMap, datePickerCallbacks])

  return {
    todoRenderDataList,
    folderDataMap,
    contextMenuItemsMap,
    handleDueDateChange,
  }
}
